<script setup>
import { computed, ref, defineProps, defineEmits } from 'vue'
import { CheckIcon, ChevronUpIcon, ChevronDownIcon, SearchIcon } from '@heroicons/vue/solid'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'

const props = defineProps(['modelValue', 'options', 'label', 'noCreate'])
const emit = defineEmits(['update:modelValue', 'createVariable'])

const query = ref('')
const selectedVariable = computed({
    get: () => props.modelValue || 0,
    set: (a) => {
        emit('update:modelValue', a)
        query.value = ''
    }
})

const filteredList = computed(() => {
  if (!props.options) {
    return []
  }

  if (!query.value) {
    return props.options
  }

  return props.options.filter(x => x.name.toLowerCase().includes(query.value.toLowerCase()))
})

function clearSelection() {
  emit('update:modelValue', null)
}

</script>

<template>
<Combobox as="div" v-model="selectedVariable">
    <div class="relative" >
      <div class="flex items-center border-b-2 border-gray-300">
        <SearchIcon class="h-5 text-gray-400"/>

        <ComboboxInput
          v-bind="$attrs"
          class="w-full border-0 bg-transparent py-1.5 pl-2 pr-10 text-gray-700 ring-gray-100 focus:ring-0 focus:border-gray-300 placeholder-gray-400" 
          placeholder="Search filters"
          @change="query = $event.target.value"
          :displayValue="(x) => x.name"
        />

        <div
          v-show="props.modelValue"
          class="absolute right-6 text-gray-400 cursor-pointer"
          @click="clearSelection()"
        >
          x
        </div>
      </div>


      <ComboboxButton :class="[props.label ? 'inset-y-12' : 'inset-y-0']" class="absolute right-0 flex flex-col justify-center items-center focus:outline-none">
        <ChevronUpIcon class="h-4 -mb-1 text-gray-400" aria-hidden="true" />
        <ChevronDownIcon class="h-4 -mt-1 text-gray-400" aria-hidden="true" />
      </ComboboxButton>

      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
        >
      <ComboboxOptions v-show="filteredList.length > 0" class="absolute w-full z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-sm">
        
        <ComboboxOption v-if="query.length > 0 && filteredList.length == 0 && !noCreate" :value="query">
            <li class="relative cursor-default select-none py-1 pl-3 pr-9 hover:bg-blue-900 hover:text-white">
                Create "{{ query }}"
            </li>
        </ComboboxOption>

        <ComboboxOption v-for="variable in filteredList" :key="variable.id" :value="variable" as="template" v-slot="{ active, selected }">
          <li :class="['relative cursor-default select-none py-1 pl-3 pr-9', active ? 'bg-blue-900 text-white' : 'text-gray-900']">
            <span :class="['block truncate', selected && 'font-semibold']">
              {{ variable.name }}
            </span>

            <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-blue-900']">
              <CheckIcon class="h-5 w-5" aria-hidden="true" />
            </span>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
      </transition>
    </div>
</Combobox>
</template>